// First-party analytics tracker (cookieless until consent, lightweight).
// Pageviews, events, engagement (time + scroll) -> /api/public/track.

import { AI_REFERRAL_MEDIUM, aiSurfaceFromHost, isAmbiguousAiSurface } from "./ai-referral";
import { gaEvent } from "./ga";


const VISITOR_KEY = "fenny_vid";
const SESSION_KEY = "fenny_sid";
const SESSION_TS_KEY = "fenny_sid_ts";
const ATTRIB_KEY = "fenny_attrib";
const SCROLL_FLAGS_KEY = "fenny_scroll_flags"; // session-scoped
const SESSION_IDLE_MS = 30 * 60 * 1000;

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getConsent(): "all" | "essential" | null {
  if (typeof window === "undefined") return null;
  try {
    return (localStorage.getItem("fenny_consent") as "all" | "essential" | null) ?? null;
  } catch {
    return null;
  }
}

function getOrCreate(storage: Storage, key: string): string {
  let v = storage.getItem(key);
  if (!v) {
    v = uuid();
    storage.setItem(key, v);
  }
  return v;
}

export interface AttribSnapshot {
  visitor_id: string | null;
  session_id: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  landing_path: string | null;
  referrer_host: string | null;
}

function captureAttribFromUrl(): Partial<AttribSnapshot> | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const get = (k: string) => params.get(k);
  const hasUtm = !!(get("utm_source") || get("utm_medium") || get("utm_campaign"));
  let referrer_host: string | null = null;
  try {
    if (document.referrer) {
      const u = new URL(document.referrer);
      if (u.hostname !== window.location.hostname) referrer_host = u.hostname;
    }
  } catch {}
  if (!hasUtm && !referrer_host) return null;
  // AI-hänvisningar märks upp så att leads och bokningar kan mätas separat.
  const aiSurface = aiSurfaceFromHost(referrer_host);
  const countAsAi = !!aiSurface && !isAmbiguousAiSurface(aiSurface);
  return {
    utm_source: get("utm_source") ?? (countAsAi ? aiSurface : null),
    utm_medium: get("utm_medium") ?? (countAsAi ? AI_REFERRAL_MEDIUM : null),
    utm_campaign: get("utm_campaign"),
    utm_term: get("utm_term"),
    utm_content: get("utm_content"),
    landing_path: window.location.pathname,
    referrer_host,
  };

}

function ensureIds(): { visitor_id: string; session_id: string; isNewSession: boolean } | null {
  const consent = getConsent();
  if (consent !== "all") return null;
  try {
    const visitor_id = getOrCreate(localStorage, VISITOR_KEY);
    const now = Date.now();
    const last = parseInt(sessionStorage.getItem(SESSION_TS_KEY) ?? "0", 10);
    let session_id = sessionStorage.getItem(SESSION_KEY);
    let isNewSession = false;
    if (!session_id || now - last > SESSION_IDLE_MS) {
      session_id = uuid();
      sessionStorage.setItem(SESSION_KEY, session_id);
      sessionStorage.removeItem(SCROLL_FLAGS_KEY);
      isNewSession = true;
    }
    sessionStorage.setItem(SESSION_TS_KEY, String(now));
    return { visitor_id, session_id, isNewSession };
  } catch {
    return null;
  }
}

function persistAttrib(snap: Partial<AttribSnapshot>, ids: { visitor_id: string; session_id: string }) {
  try {
    const existing = localStorage.getItem(ATTRIB_KEY);
    if (existing) {
      const parsed = JSON.parse(existing) as AttribSnapshot;
      parsed.session_id = ids.session_id;
      parsed.visitor_id = ids.visitor_id;
      localStorage.setItem(ATTRIB_KEY, JSON.stringify(parsed));
      return;
    }
    const full: AttribSnapshot = {
      visitor_id: ids.visitor_id,
      session_id: ids.session_id,
      utm_source: snap.utm_source ?? null,
      utm_medium: snap.utm_medium ?? null,
      utm_campaign: snap.utm_campaign ?? null,
      utm_term: snap.utm_term ?? null,
      utm_content: snap.utm_content ?? null,
      landing_path: snap.landing_path ?? null,
      referrer_host: snap.referrer_host ?? null,
    };
    localStorage.setItem(ATTRIB_KEY, JSON.stringify(full));
  } catch {}
}

export function getAttribSnapshot(): AttribSnapshot {
  if (typeof window === "undefined") {
    return {
      visitor_id: null, session_id: null, utm_source: null, utm_medium: null,
      utm_campaign: null, utm_term: null, utm_content: null, landing_path: null, referrer_host: null,
    };
  }
  try {
    const raw = localStorage.getItem(ATTRIB_KEY);
    if (raw) return JSON.parse(raw) as AttribSnapshot;
  } catch {}
  let visitor_id: string | null = null;
  let session_id: string | null = null;
  try {
    if (getConsent() === "all") {
      visitor_id = localStorage.getItem(VISITOR_KEY);
      session_id = sessionStorage.getItem(SESSION_KEY);
    }
  } catch {}
  return {
    visitor_id, session_id,
    utm_source: null, utm_medium: null, utm_campaign: null, utm_term: null,
    utm_content: null, landing_path: null, referrer_host: null,
  };
}

function sendBeacon(payload: unknown) {
  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon("/api/public/track", blob);
      return;
    }
  } catch {}
  fetch("/api/public/track", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

// ---------- Engagement tracking (per page) ----------

interface PageEngagement {
  path: string;
  startedAt: number;
  visibleMs: number;
  lastVisibleStart: number | null;
  maxScrollPct: number;
}

let currentPage: PageEngagement | null = null;
let engagementInitDone = false;

function calcVisibleMs(): number {
  if (!currentPage) return 0;
  let total = currentPage.visibleMs;
  if (currentPage.lastVisibleStart !== null) {
    total += Date.now() - currentPage.lastVisibleStart;
  }
  return total;
}

function flushEngagement(isExit: boolean) {
  if (!currentPage) return;
  const ids = ensureIds();
  if (!ids) return;
  const time_on_page_ms = Math.min(calcVisibleMs(), 30 * 60 * 1000);
  if (time_on_page_ms < 250 && currentPage.maxScrollPct === 0 && !isExit) return;
  sendBeacon({
    type: "engagement",
    visitor_id: ids.visitor_id,
    session_id: ids.session_id,
    path: currentPage.path,
    time_on_page_ms,
    max_scroll_pct: Math.round(currentPage.maxScrollPct),
    is_exit: isExit,
  });
  // Mark as flushed so we don't double-count on pagehide after route change.
  currentPage.visibleMs = 0;
  currentPage.lastVisibleStart = document.visibilityState === "visible" ? Date.now() : null;
}

function computeScrollPct(): number {
  const doc = document.documentElement;
  const winH = window.innerHeight || doc.clientHeight;
  const docH = Math.max(doc.scrollHeight, doc.offsetHeight, doc.clientHeight);
  if (docH <= winH) return 100;
  const scrolled = (window.scrollY || doc.scrollTop) + winH;
  return Math.max(0, Math.min(100, (scrolled / docH) * 100));
}

function getScrollFlags(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SCROLL_FLAGS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveScrollFlags(flags: Set<string>) {
  try {
    sessionStorage.setItem(SCROLL_FLAGS_KEY, JSON.stringify([...flags]));
  } catch {}
}

function onScroll() {
  if (!currentPage) return;
  const pct = computeScrollPct();
  if (pct > currentPage.maxScrollPct) currentPage.maxScrollPct = pct;
  // Milestone events: 25/50/75/100 per path per session
  const flags = getScrollFlags();
  const milestones = [25, 50, 75, 100];
  for (const m of milestones) {
    const key = `${currentPage.path}@${m}`;
    if (pct >= m && !flags.has(key)) {
      flags.add(key);
      trackEvent({
        name: "scroll_depth",
        category: "engagement",
        label: String(m),
        value: m,
      });
    }
  }
  saveScrollFlags(flags);
}

function onVisibility() {
  if (!currentPage) return;
  if (document.visibilityState === "hidden") {
    if (currentPage.lastVisibleStart !== null) {
      currentPage.visibleMs += Date.now() - currentPage.lastVisibleStart;
      currentPage.lastVisibleStart = null;
    }
    flushEngagement(false);
  } else {
    currentPage.lastVisibleStart = Date.now();
  }
}

function onPageHide() {
  flushEngagement(true);
}

function initEngagement() {
  if (engagementInitDone || typeof window === "undefined") return;
  engagementInitDone = true;
  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("beforeunload", onPageHide);
}

// ---------- Public API ----------

export function trackPageview(path?: string) {
  if (typeof window === "undefined") return;
  const ids = ensureIds();
  if (!ids) return;

  const snap = captureAttribFromUrl();
  if (snap) persistAttrib(snap, ids);

  let referrer_host: string | null = null;
  try {
    if (document.referrer) {
      const u = new URL(document.referrer);
      if (u.hostname !== window.location.hostname) referrer_host = u.hostname;
    }
  } catch {}

  const nextPath = path ?? window.location.pathname;

  // Flush the previous page's engagement before switching.
  if (currentPage && currentPage.path !== nextPath) {
    flushEngagement(false);
  }

  initEngagement();
  currentPage = {
    path: nextPath,
    startedAt: Date.now(),
    visibleMs: 0,
    lastVisibleStart: document.visibilityState === "visible" ? Date.now() : null,
    maxScrollPct: 0,
  };

  sendBeacon({
    type: "pageview",
    visitor_id: ids.visitor_id,
    session_id: ids.session_id,
    is_new_session: ids.isNewSession,
    path: nextPath,
    title: document.title || null,
    referrer: document.referrer || null,
    referrer_host,
    utm_source: snap?.utm_source ?? null,
    utm_medium: snap?.utm_medium ?? null,
    utm_campaign: snap?.utm_campaign ?? null,
    utm_term: snap?.utm_term ?? null,
    utm_content: snap?.utm_content ?? null,
    landing_path: ids.isNewSession ? nextPath : null,
    language: navigator.language || null,
    user_agent: navigator.userAgent || null,
  });
}

export interface TrackEventInput {
  name: string;
  category?: string;
  label?: string | null;
  value?: number | null;
  meta?: Record<string, unknown> | null;
}

export function trackEvent(ev: TrackEventInput) {
  if (typeof window === "undefined") return;
  const ids = ensureIds();
  if (!ids) return;
  sendBeacon({
    type: "event",
    visitor_id: ids.visitor_id,
    session_id: ids.session_id,
    path: window.location.pathname,
    name: ev.name.slice(0, 80),
    category: ev.category ? ev.category.slice(0, 40) : null,
    label: ev.label ? String(ev.label).slice(0, 200) : null,
    value: typeof ev.value === "number" ? ev.value : null,
    meta: ev.meta ?? null,
  });

  // Spegla till Google Analytics 4 så samma händelser syns där.
  gaEvent(ev.name.slice(0, 40), {
    event_category: ev.category ?? "ui",
    event_label: ev.label ? String(ev.label).slice(0, 100) : undefined,
    value: typeof ev.value === "number" ? ev.value : undefined,
    visitor_id: ids.visitor_id,
    session_id: ids.session_id,
  });
}

export function clearAnalyticsIds() {
  try {
    localStorage.removeItem(VISITOR_KEY);
    localStorage.removeItem(ATTRIB_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_TS_KEY);
    sessionStorage.removeItem(SCROLL_FLAGS_KEY);
  } catch {}
}
